import type {
  Category,
  Item,
  MenuCollection,
  MenuData,
  MenuSection,
  PagedFlipCategoryLayout,
  PagedFlipCategoryRule,
  PagedFlipConfig,
  PagedFlipContentFlow,
  PagedFlipContentLayout,
  PagedFlipPageSlot,
  PagedFlipSpotlightImageSource,
  Template,
} from '@/app/dashboard/templates/components/types/template';
import type {
  PagedFlipCategoryBlock,
  PagedFlipChapter,
  PagedFlipDocument,
  PagedFlipMeasurementSnapshot,
  PagedFlipMetaRow,
  PagedFlipPage,
  PagedFlipPageItem,
} from '@/lib/mnrq/paged-flip/types';
import { isPromotionActive } from '@/lib/promotions/scheduler';

// Pagination maths (TPL-04) lives in ./pagination so it can be unit-tested
// without rendering. `ensureEvenPagedFlipPages` is re-exported to preserve the
// public API consumed by PagedFlipRenderer.
import {
  ensureEvenPagedFlipPages,
  hasRenderableText,
  pageHasFlowItems,
  pageHasRenderableContent,
  removeEmptyPages,
  resequencePagedFlipPages,
} from './pagination';
export { ensureEvenPagedFlipPages } from './pagination';

type ViewportMode = 'desktop' | 'mobile';
type ContentPageKind = Extract<PagedFlipPage['kind'], 'content' | 'featured-showcase' | 'new-arrivals'>;

type LayoutWeightProfile = {
  pageBaseWeight: number;
  categoryFrameWeight: number;
  itemDescriptionFactor: number;
  itemDescriptionCap: number;
  categoryDescriptionFactor: number;
  categoryDescriptionCap: number;
  imageWeight: number;
  promoWeight: number;
  featuredWeight: number;
  newWeight: number;
  comingSoonWeight: number;
  baseItemWeight: number;
};

const LEGACY_PAGED_FLIP_NUMERIC_CONFIG_KEYS = [
  'maxItemsPerPage',
  'maxCategoryBlocksPerPage',
  'desktopMaxPageWeight',
  'mobileMaxPageWeight',
  'newItemWindowDays',
  'minPageFillRatio',
] as const;

const DEFAULT_NEW_ITEM_WINDOW_DAYS = 7;
const DEFAULT_DESKTOP_PAGE_HEIGHT = 980;
const DEFAULT_MOBILE_PAGE_HEIGHT = 820;

type RequiredPagedFlipConfig = Required<
  Pick<
    PagedFlipConfig,
    | 'showCoverPage'
    | 'showTableOfContents'
    | 'chapterIntroPages'
    | 'showBackCover'
    | 'showSectionDividerPages'
    | 'showFeaturedItemPages'
    | 'showNewItemPages'
    | 'useCollectionsAsChapters'
    | 'keepCategoryTogether'
    | 'desktopSpread'
    | 'mobileDisableTouchFlip'
    | 'mobileHideCategorySpotlight'
    | 'showItemDescriptions'
    | 'showPromotionBadges'
    | 'showCategoryImages'
    | 'showCategorySpotlights'
    | 'categorySpotlightImageSource'
    | 'contentLayout'
    | 'contentFlow'
    | 'featuredInsertPosition'
    | 'newItemsInsertPosition'
    | 'promotionPages'
    | 'featuredPages'
    | 'newItemPages'
    | 'sectionDivider'
    | 'slotPolicy'
    | 'images'
  >
> & {
  pageHeight?: number;
};

const DEFAULT_CONFIG: RequiredPagedFlipConfig = {
  showCoverPage: true,
  showTableOfContents: true,
  chapterIntroPages: true,
  showBackCover: false,
  showSectionDividerPages: true,
  showFeaturedItemPages: true,
  showNewItemPages: false,
  useCollectionsAsChapters: true,
  keepCategoryTogether: false,
  desktopSpread: true,
  mobileDisableTouchFlip: true,
  mobileHideCategorySpotlight: true,
  showItemDescriptions: true,
  showPromotionBadges: true,
  showCategoryImages: true,
  showCategorySpotlights: true,
  categorySpotlightImageSource: 'auto',
  contentLayout: 'stacked',
  contentFlow: 'category-first',
  featuredInsertPosition: 'after-category',
  newItemsInsertPosition: 'after-category',
  promotionPages: {
    enabled: false,
    insertPosition: 'before-content',
    title: 'Current Offers',
    subtitle: 'Discover active promotions curated for this chapter.',
    eyebrow: 'Promotions',
  },
  featuredPages: {
    enabled: true,
    title: 'Featured Items',
    subtitle: 'Signature selections curated for this chapter.',
    eyebrow: 'Featured',
  },
  newItemPages: {
    enabled: true,
    title: 'New Arrivals',
    subtitle: 'Fresh additions and time-limited releases.',
    eyebrow: 'New',
  },
  sectionDivider: {
    enabled: true,
    showMetaRows: true,
  },
  slotPolicy: {
    requireExplicitSlots: true,
    requiredSlots: ['content'] as PagedFlipPageSlot[],
  },
  images: {
    fit: 'cover',
    loading: 'lazy',
    optimize: 'auto',
    quality: 80,
    sizes: '(max-width: 768px) 100vw, 50vw',
    prioritySlots: ['cover'],
  },
};

export function resolvePagedFlipConfig(template?: Template | null): RequiredPagedFlipConfig & PagedFlipConfig {
  const legacyNumericEntries = Object.fromEntries(
    LEGACY_PAGED_FLIP_NUMERIC_CONFIG_KEYS.map((key) => [key, undefined]),
  ) as Partial<PagedFlipConfig>;
  const promotionPages = {
    ...DEFAULT_CONFIG.promotionPages,
    ...(template?.pagedFlip?.promotionPages || {}),
  };
  const featuredPages = {
    ...DEFAULT_CONFIG.featuredPages,
    ...(template?.pagedFlip?.featuredPages || {}),
  };
  const newItemPages = {
    ...DEFAULT_CONFIG.newItemPages,
    ...(template?.pagedFlip?.newItemPages || {}),
  };
  const sectionDivider = {
    ...DEFAULT_CONFIG.sectionDivider,
    ...(template?.pagedFlip?.sectionDivider || {}),
  };
  const slotPolicy = {
    ...DEFAULT_CONFIG.slotPolicy,
    ...(template?.pagedFlip?.slotPolicy || {}),
  };
  const images = {
    ...DEFAULT_CONFIG.images,
    ...(template?.pagedFlip?.images || {}),
  };

  return {
    ...DEFAULT_CONFIG,
    ...(template?.pagedFlip || {}),
    promotionPages,
    featuredPages,
    newItemPages,
    sectionDivider,
    slotPolicy,
    images,
    ...legacyNumericEntries,
  };
}

type PagedFlipRuntimeConfig = ReturnType<typeof resolvePagedFlipConfig> & {
  pageHeight?: number;
  measurementScopeKey?: string;
  measurements?: PagedFlipMeasurementSnapshot;
};

function normalizeMeasurementScopeKey(value: string | undefined) {
  const trimmed = String(value || '').trim();
  return trimmed || 'default';
}

export function buildPagedFlipMeasurementBucketKey(
  measurementScopeKey: string | undefined,
  viewport: ViewportMode,
  kind: PagedFlipPage['kind'],
  layout: PagedFlipContentLayout = 'stacked',
) {
  return `${normalizeMeasurementScopeKey(measurementScopeKey)}:${viewport}:${kind}:${layout}`;
}

export function buildPagedFlipMeasuredItemKey(bucketKey: string, itemId: string) {
  return `${bucketKey}:item:${itemId}`;
}

export function buildPagedFlipMeasuredCategoryKey(
  bucketKey: string,
  categoryId: string,
  continuation: boolean,
) {
  return `${bucketKey}:category:${categoryId}:${continuation ? 'continuation' : 'initial'}`;
}

function resolveMeasurementBucketKey(
  config: PagedFlipRuntimeConfig,
  viewport: ViewportMode,
  kind: PagedFlipPage['kind'],
  layout: PagedFlipContentLayout,
) {
  return buildPagedFlipMeasurementBucketKey(config.measurementScopeKey, viewport, kind, layout);
}

function normalizePrice(item: Item): string | undefined {
  if (item.price === undefined || item.price === null || item.price === '') return undefined;
  return `${item.currency || ''}${item.price}`.trim();
}

function normalizeTag(value: unknown): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, '-');
}

function isTimestampWithinWindow(timestamp: number, windowDays: number) {
  if (!Number.isFinite(timestamp)) return false;
  const now = Date.now();
  const windowMs = Math.max(windowDays, 1) * 24 * 60 * 60 * 1000;
  return Math.abs(now - timestamp) <= windowMs;
}

function isNewItem(item: Item, windowDays: number) {
  if (!item.created_at) return false;
  const timestamp = new Date(item.created_at).getTime();
  return Number.isFinite(timestamp) && Date.now() - timestamp <= windowDays * 24 * 60 * 60 * 1000;
}

function isComingSoonItem(item: Item, windowDays: number) {
  if (item.is_coming_soon || item.coming_soon) return true;

  const taggedComingSoon = (item.tags || []).some((tag) => {
    const normalized = normalizeTag(tag);
    return normalized === 'coming-soon' || normalized === 'comingsoon' || normalized === 'upcoming';
  });

  if (taggedComingSoon) return true;

  if (!item.available_from) return false;

  const availableFrom = new Date(item.available_from).getTime();
  return availableFrom > Date.now() && isTimestampWithinWindow(availableFrom, windowDays);
}

function resolveLayoutWeightProfile(
  layout: PagedFlipContentLayout,
  viewport: ViewportMode,
  kind: ContentPageKind = 'content',
): LayoutWeightProfile {
  const mobile = viewport === 'mobile';

  let profile: LayoutWeightProfile = {
    pageBaseWeight: mobile ? 120 : 180,
    categoryFrameWeight: mobile ? 72 : 124,
    itemDescriptionFactor: mobile ? 24 : 28,
    itemDescriptionCap: mobile ? 120 : 152,
    categoryDescriptionFactor: mobile ? 24 : 28,
    categoryDescriptionCap: mobile ? 96 : 128,
    imageWeight: mobile ? 148 : 208,
    promoWeight: mobile ? 32 : 40,
    featuredWeight: 24,
    newWeight: 20,
    comingSoonWeight: 20,
    baseItemWeight: mobile ? 62 : 82,
  };

  if (kind === 'featured-showcase' || kind === 'new-arrivals') {
    profile = {
      ...profile,
      pageBaseWeight: mobile ? 90 : 120,
      categoryFrameWeight: mobile ? 42 : 52,
      itemDescriptionFactor: mobile ? 8 : 10,
      itemDescriptionCap: mobile ? 28 : 36,
      categoryDescriptionFactor: mobile ? 10 : 12,
      categoryDescriptionCap: mobile ? 28 : 40,
      imageWeight: mobile ? 24 : 32,
      promoWeight: mobile ? 10 : 12,
      featuredWeight: 8,
      newWeight: 8,
      comingSoonWeight: 10,
      baseItemWeight: mobile ? 26 : 32,
    };
  } else if (layout === 'two-column-items') {
    profile = {
      ...profile,
      pageBaseWeight: profile.pageBaseWeight - (mobile ? 10 : 20),
      categoryFrameWeight: mobile ? 56 : 80,
      itemDescriptionFactor: mobile ? 24 : 28,
      itemDescriptionCap: mobile ? 140 : 180,
      imageWeight: mobile ? 120 : 160,
      promoWeight: mobile ? 28 : 34,
      baseItemWeight: mobile ? 64 : 80,
    };
  } else if (layout === 'spotlight-spread') {
    profile = {
      ...profile,
      pageBaseWeight: profile.pageBaseWeight + (mobile ? 20 : 40),
      categoryFrameWeight: profile.categoryFrameWeight + (mobile ? 10 : 20),
    };
  }

  if (kind !== 'content' && kind !== 'featured-showcase' && kind !== 'new-arrivals') {
    profile = {
      ...profile,
      pageBaseWeight: profile.pageBaseWeight + (mobile ? 40 : 60),
      categoryFrameWeight: profile.categoryFrameWeight + 20,
    };
  }

  return profile;
}

function resolvePromoLabel(item: Item, category: Category | undefined, config: ReturnType<typeof resolvePagedFlipConfig>): string | undefined {
  if (config.showPromotionBadges === false) return undefined;
  return (
    item.promotion?.badge_label ||
    item.promotion?.discount_label ||
    item.promotion?.custom_label ||
    item.promotion?.title ||
    category?.promotion?.badge_label ||
    category?.promotion?.discount_label ||
    category?.promotion?.custom_label ||
    category?.promotion?.title ||
    undefined
  );
}

function resolveCategoryPromoLabel(category: Category | undefined, config: ReturnType<typeof resolvePagedFlipConfig>): string | undefined {
  if (!category || config.showPromotionBadges === false) return undefined;
  return (
    category.promotion?.badge_label ||
    category.promotion?.discount_label ||
    category.promotion?.custom_label ||
    category.promotion?.title ||
    undefined
  );
}

function toPageItem(item: Item, category: Category | undefined, config: ReturnType<typeof resolvePagedFlipConfig>): PagedFlipPageItem {
  const newItemWindowDays = Number(config.newItemWindowDays || DEFAULT_NEW_ITEM_WINDOW_DAYS);
  return {
    id: item.id,
    title: item.title || item.name || 'Untitled item',
    description: config.showItemDescriptions === false ? undefined : item.description,
    price: normalizePrice(item),
    image: config.showCategoryImages === false ? undefined : item.image,
    promoLabel: resolvePromoLabel(item, category, config),
    promotion: item.promotion,
    featured: Boolean(item.is_featured),
    isNew: isNewItem(item, newItemWindowDays),
    comingSoon: isComingSoonItem(item, newItemWindowDays),
    sellState: item.sell_state,
    sellStateReason: item.sell_state_reason || undefined,
    availableForOrder: item.available_for_order,
    availabilityLabel: item.availability_label,
    createdAt: item.created_at,
  };
}

function buildImplicitCollection(menuData: MenuData): MenuCollection {
  return {
    id: '__default_collection__',
    name: menuData.menu.title || 'Menu',
    slug: 'menu',
    description: menuData.menu.description,
    is_default: true,
    categories: menuData.menu.categories || [],
    items: menuData.menu.items || menuData.items || [],
    featuredItems: menuData.menu.featuredItems || menuData.featuredItems || [],
    sections: menuData.menu.sections || [],
    categoriesBySection: menuData.menu.categoriesBySection,
  };
}

function getCollections(menuData: MenuData, useCollectionsAsChapters: boolean): MenuCollection[] {
  const explicitCollections = Array.isArray(menuData.menu.collections) ? menuData.menu.collections : [];
  if (useCollectionsAsChapters && explicitCollections.length > 0) {
    return explicitCollections.filter((collection) => (collection.categories || []).length > 0);
  }
  return [buildImplicitCollection(menuData)];
}

function estimateItemWeight(item: PagedFlipPageItem, profile: LayoutWeightProfile): number {
  let weight = profile.baseItemWeight;
  if (item.description) {
    weight += Math.min(
      profile.itemDescriptionCap,
      Math.ceil(item.description.length / 50) * profile.itemDescriptionFactor,
    );
  }
  if (item.promoLabel) {
    weight += profile.promoWeight;
  }
  if (item.featured) {
    weight += profile.featuredWeight;
  }
  if (item.isNew) {
    weight += profile.newWeight;
  }
  if (item.comingSoon) {
    weight += profile.comingSoonWeight;
  }
  if (item.image) {
    weight += profile.imageWeight;
  }
  return weight;
}

function resolveMeasuredPageBaseWeight(
  config: PagedFlipRuntimeConfig,
  viewport: ViewportMode,
  layout: PagedFlipContentLayout,
  kind: ContentPageKind,
  fallbackWeight: number,
): number {
  const bucketKey = resolveMeasurementBucketKey(config, viewport, kind, layout);
  const measuredWeight = Number(config.measurements?.pageBaseWeights?.[bucketKey]);
  const pageBaseWeight = typeof measuredWeight === 'number' && Number.isFinite(measuredWeight) && measuredWeight >= 0
    ? measuredWeight
    : fallbackWeight;
  return pageBaseWeight;
}

function resolveItemWeight(
  item: PagedFlipPageItem,
  profile: LayoutWeightProfile,
  config: PagedFlipRuntimeConfig,
  viewport: ViewportMode,
  layout: PagedFlipContentLayout,
  kind: ContentPageKind,
): number {
  const bucketKey = resolveMeasurementBucketKey(config, viewport, kind, layout);
  const measuredWeight = config.measurements?.itemWeights?.[buildPagedFlipMeasuredItemKey(bucketKey, item.id)];
  if (typeof measuredWeight === 'number' && Number.isFinite(measuredWeight) && measuredWeight >= 0) {
    return measuredWeight;
  }

  return estimateItemWeight(item, profile);
}

function estimateCategoryHeaderWeight(
  category: Pick<Category, 'description'>,
  viewport: ViewportMode,
  profile: LayoutWeightProfile,
): number {
  let weight = profile.categoryFrameWeight;
  if (category.description) {
    weight += Math.min(
      profile.categoryDescriptionCap,
      Math.ceil(category.description.length / 50) * profile.categoryDescriptionFactor,
    );
  }
  return weight;
}

function resolveCategoryHeaderWeight(
  category: Pick<Category, 'id' | 'description'>,
  viewport: ViewportMode,
  profile: LayoutWeightProfile,
  config: PagedFlipRuntimeConfig,
  layout: PagedFlipContentLayout,
  kind: ContentPageKind,
  continuation: boolean,
): number {
  const bucketKey = resolveMeasurementBucketKey(config, viewport, kind, layout);
  const measuredWeight =
    config.measurements?.categoryHeaderWeights?.[
      buildPagedFlipMeasuredCategoryKey(bucketKey, category.id, continuation)
    ];

  if (typeof measuredWeight === 'number' && Number.isFinite(measuredWeight) && measuredWeight >= 0) {
    return measuredWeight;
  }

  const fallbackWeight = estimateCategoryHeaderWeight(category, viewport, profile);
  return continuation ? fallbackWeight * 0.4 : fallbackWeight;
}

function estimateCategoryFooterWeight(viewport: ViewportMode): number {
  return viewport === 'mobile' ? 12 : 16;
}

function resolveCategoryFooterWeight(
  category: Pick<Category, 'id'>,
  viewport: ViewportMode,
  config: PagedFlipRuntimeConfig,
  layout: PagedFlipContentLayout,
  kind: ContentPageKind,
  continuation: boolean,
): number {
  const bucketKey = resolveMeasurementBucketKey(config, viewport, kind, layout);
  const measuredWeight =
    config.measurements?.categoryFooterWeights?.[
      buildPagedFlipMeasuredCategoryKey(bucketKey, category.id, continuation)
    ];

  if (typeof measuredWeight === 'number' && Number.isFinite(measuredWeight) && measuredWeight >= 0) {
    return measuredWeight;
  }

  return estimateCategoryFooterWeight(viewport);
}

type WeightedCategoryBlock = PagedFlipCategoryBlock & {
  estimatedWeight: number;
  startsAt: number;
  endsAt: number;
  totalItems: number;
};

function resolveViewportPageWeight(
  config: PagedFlipRuntimeConfig,
  viewport: ViewportMode,
): number {
  if (config.pageHeight && config.pageHeight > 0) return config.pageHeight;
  return viewport === 'mobile' ? DEFAULT_MOBILE_PAGE_HEIGHT : DEFAULT_DESKTOP_PAGE_HEIGHT;
}

function resolvePageWeightBudget(
  config: PagedFlipRuntimeConfig,
  viewport: ViewportMode,
  layout: PagedFlipContentLayout,
  kind: ContentPageKind = 'content',
): {
  maxPageWeight: number;
  pageBaseWeight: number;
  usablePageWeight: number;
  profile: LayoutWeightProfile;
} {
  const maxPageWeight = resolveViewportPageWeight(config, viewport);
  const rawProfile = resolveLayoutWeightProfile(layout, viewport, kind);
  
  // Scale the weight profile if the actual page height differs from the default.
  // The estimated weights (baseItemWeight: 72, imageWeight: 200, etc.) were calibrated
  // for DEFAULT_DESKTOP_PAGE_HEIGHT (980px) / DEFAULT_MOBILE_PAGE_HEIGHT (820px).
  // When the page is shorter (e.g. 682px because of viewport chrome), we need to scale
  // estimates down proportionally since the template renderer compresses content.
  const defaultHeight = viewport === 'mobile' ? DEFAULT_MOBILE_PAGE_HEIGHT : DEFAULT_DESKTOP_PAGE_HEIGHT;
  const scaleFactor = maxPageWeight < defaultHeight ? maxPageWeight / defaultHeight : 1;
  
  const profile: LayoutWeightProfile = scaleFactor < 1 ? {
    pageBaseWeight: Math.round(rawProfile.pageBaseWeight * scaleFactor),
    categoryFrameWeight: Math.round(rawProfile.categoryFrameWeight * scaleFactor),
    itemDescriptionFactor: Math.round(rawProfile.itemDescriptionFactor * scaleFactor),
    itemDescriptionCap: Math.round(rawProfile.itemDescriptionCap * scaleFactor),
    categoryDescriptionFactor: Math.round(rawProfile.categoryDescriptionFactor * scaleFactor),
    categoryDescriptionCap: Math.round(rawProfile.categoryDescriptionCap * scaleFactor),
    imageWeight: Math.round(rawProfile.imageWeight * scaleFactor),
    promoWeight: Math.round(rawProfile.promoWeight * scaleFactor),
    featuredWeight: Math.round(rawProfile.featuredWeight * scaleFactor),
    newWeight: Math.round(rawProfile.newWeight * scaleFactor),
    comingSoonWeight: Math.round(rawProfile.comingSoonWeight * scaleFactor),
    baseItemWeight: Math.round(rawProfile.baseItemWeight * scaleFactor),
  } : rawProfile;
  
  const pageBaseWeight = resolveMeasuredPageBaseWeight(config, viewport, layout, kind, profile.pageBaseWeight);

  return {
    maxPageWeight,
    pageBaseWeight,
    usablePageWeight: Math.max(200, maxPageWeight - pageBaseWeight),
    profile,
  };
}

function enrichCategoryBlock(
  block: WeightedCategoryBlock,
  usablePageWeight: number,
): PagedFlipCategoryBlock {
  const promoCount = block.items.filter((item) => Boolean(item.promoLabel)).length;
  const featuredCount = block.items.filter((item) => Boolean(item.featured)).length;
  const newCount = block.items.filter((item) => Boolean(item.isNew)).length;
  const comingSoonCount = block.items.filter((item) => Boolean(item.comingSoon)).length;
  const imageCount = block.items.filter((item) => Boolean(item.image)).length;

  return {
    id: block.id,
    sourceCategoryId: block.sourceCategoryId,
    name: block.name,
    description: block.description,
    image: block.image,
    promoLabel: block.promoLabel,
    promotion: block.promotion,
    items: block.items,
    continuation: block.continuation,
    metrics: {
      itemCount: block.items.length,
      promoCount,
      featuredCount,
      newCount,
      comingSoonCount,
      imageCount,
      totalWeight: block.estimatedWeight,
      fillRatio: usablePageWeight > 0 ? Math.min(block.estimatedWeight / usablePageWeight, 1) : 0,
      startsAt: block.startsAt,
      endsAt: block.endsAt,
      totalItems: block.totalItems,
    },
  };
}

function buildPageMetrics(
  blocks: WeightedCategoryBlock[],
  maxPageWeight: number,
  pageBaseWeight: number,
): PagedFlipPage['metrics'] {
  const itemCount = blocks.reduce((sum, block) => sum + block.items.length, 0);
  const promoCount = blocks.reduce(
    (sum, block) => sum + block.items.filter((item) => Boolean(item.promoLabel)).length,
    0,
  );
  const featuredCount = blocks.reduce(
    (sum, block) => sum + block.items.filter((item) => Boolean(item.featured)).length,
    0,
  );
  const newCount = blocks.reduce(
    (sum, block) => sum + block.items.filter((item) => Boolean(item.isNew)).length,
    0,
  );
  const comingSoonCount = blocks.reduce(
    (sum, block) => sum + block.items.filter((item) => Boolean(item.comingSoon)).length,
    0,
  );
  const imageCount = blocks.reduce(
    (sum, block) => sum + block.items.filter((item) => Boolean(item.image)).length,
    0,
  );
  const contentWeight = blocks.reduce((sum, block) => sum + block.estimatedWeight, 0);
  const totalWeight = contentWeight + pageBaseWeight;
  const continuationCount = blocks.filter((block) => Boolean(block.continuation)).length;

  return {
    itemCount,
    categoryCount: blocks.length,
    promoCount,
    featuredCount,
    newCount,
    comingSoonCount,
    imageCount,
    totalWeight,
    fillRatio: maxPageWeight > 0 ? Math.min(totalWeight / maxPageWeight, 1) : 0,
    continuationCount,
    pageBaseWeight,
    usablePageWeight: Math.max(0, maxPageWeight - pageBaseWeight),
  };
}

function buildCategoryBlocks(
  category: Category,
  config: PagedFlipRuntimeConfig,
  viewport: ViewportMode,
  layout: PagedFlipContentLayout,
  kind: ContentPageKind = 'content',
): WeightedCategoryBlock[] {
  const budget = resolvePageWeightBudget(config, viewport, layout, kind);
  const items = (category.items || []).map((item) => {
    const pageItem = toPageItem(item, category, config);
    return {
      ...pageItem,
      estimatedWeight: resolveItemWeight(pageItem, budget.profile, config, viewport, layout, kind),
    };
  });
  const maxWeight = budget.usablePageWeight;
  const headerWeight = resolveCategoryHeaderWeight(
    category,
    viewport,
    budget.profile,
    config,
    layout,
    kind,
    false,
  );
  const footerWeight = resolveCategoryFooterWeight(
    category,
    viewport,
    config,
    layout,
    kind,
    false,
  );

  if (items.length === 0) {
    return [
      {
        id: `${category.id}-empty`,
        sourceCategoryId: category.id,
        name: category.name,
        description: config.showItemDescriptions === false ? undefined : category.description,
        image: category.image,
        promoLabel: resolveCategoryPromoLabel(category, config),
        promotion: category.promotion,
        items: [],
        estimatedWeight: headerWeight + footerWeight,
        startsAt: 0,
        endsAt: 0,
        totalItems: 0,
      },
    ];
  }

  const blocks: WeightedCategoryBlock[] = [];
  let current: PagedFlipPageItem[] = [];
  let currentWeight = headerWeight;
  let currentStartIndex = 0;
  const continuationHeaderWeight = resolveCategoryHeaderWeight(
    category,
    viewport,
    budget.profile,
    config,
    layout,
    kind,
    true,
  );
  const continuationFooterWeight = resolveCategoryFooterWeight(
    category,
    viewport,
    config,
    layout,
    kind,
    true,
  );

  const flush = (continuation: boolean) => {
    if (current.length === 0) return;
    const activeFooterWeight = continuation ? continuationFooterWeight : footerWeight;
    blocks.push({
      id: `${category.id}-${blocks.length + 1}`,
      sourceCategoryId: category.id,
      name: category.name,
      description: config.showItemDescriptions === false ? undefined : category.description,
      image: category.image,
      promoLabel: resolveCategoryPromoLabel(category, config),
      promotion: category.promotion,
      items: current,
      continuation,
      estimatedWeight: currentWeight + activeFooterWeight,
      startsAt: currentStartIndex,
      endsAt: currentStartIndex + current.length - 1,
      totalItems: items.length,
    });
    currentStartIndex += current.length;
    current = [];
    // Next block is a continuation , use reduced header weight
    currentWeight = continuationHeaderWeight;
  };

  items.forEach((item) => {
    const itemWeight = item.estimatedWeight ?? resolveItemWeight(item, budget.profile, config, viewport, layout, kind);
    const activeFooterWeight = blocks.length > 0 ? continuationFooterWeight : footerWeight;
    const wouldOverflowByWeight = current.length > 0 && currentWeight + itemWeight + activeFooterWeight > maxWeight;

    if (wouldOverflowByWeight) {
      flush(blocks.length > 0);
    }

    current.push(item);
    currentWeight += itemWeight;
  });

  flush(blocks.length > 0);
  return blocks;
}

function buildMetaRows(menuData: MenuData): PagedFlipMetaRow[] {
  const rows: PagedFlipMetaRow[] = [];
  const business = menuData.business;
  if (!business) return rows;

  rows.push({ label: 'Venue', value: business.name });

  if (business.parentName && business.parentName !== business.name) {
    rows.push({ label: 'Business', value: business.parentName });
  }
  if (business.location || business.address) {
    rows.push({ label: 'Location', value: business.location || business.address || '' });
  }
  if (business.phone) {
    rows.push({ label: 'Contact', value: business.phone });
  } else if (business.email) {
    rows.push({ label: 'Email', value: business.email });
  }
  if (business.website) {
    rows.push({ label: 'Online', value: business.website });
  }

  return rows.filter((row) => row.value.trim()).slice(0, 4);
}

function resolveFirstValidItemImage(category: Category): string | undefined {
  const image = category.items?.find((item) => typeof item.image === 'string' && item.image.trim())?.image;
  return typeof image === 'string' && image.trim() ? image.trim() : undefined;
}

function resolveValidCategoryImage(category: Category): string | undefined {
  return typeof category.image === 'string' && category.image.trim() ? category.image.trim() : undefined;
}

function resolveSpotlightImage(
  category: Category,
  config: ReturnType<typeof resolvePagedFlipConfig>,
): string | undefined {
  if (config.showCategoryImages === false) return undefined;
  const source = config.categorySpotlightImageSource as PagedFlipSpotlightImageSource;
  const firstItemImage = resolveFirstValidItemImage(category);
  const categoryImage = resolveValidCategoryImage(category);

  if (source === 'category') {
    return categoryImage || firstItemImage;
  }

  if (source === 'first-item') {
    return firstItemImage;
  }

  return categoryImage || firstItemImage;
}

type CollectionCategoryEntry = {
  category: Category;
  eyebrow?: string;
  section?: MenuSection;
  index: number;
};

type FlowCategoryBlock = PagedFlipCategoryBlock & {
  eyebrow?: string;
  estimatedWeight: number;
  startsAt: number;
  endsAt: number;
  totalItems: number;
};

function resolveFlowLimits(
  config: PagedFlipRuntimeConfig,
  viewport: ViewportMode,
  layout: PagedFlipContentLayout,
): {
  stackedBlocksAllowed: boolean;
  maxPageWeight: number;
  usablePageWeight: number;
  pageBaseWeight: number;
  blockGapWeight: number;
  profile: LayoutWeightProfile;
} {
  const budget = resolvePageWeightBudget(config, viewport, layout, 'content');
  const bucketKey = resolveMeasurementBucketKey(config, viewport, 'content', layout);
  const measuredBlockGap = Number(config.measurements?.blockGapWeights?.[bucketKey]);
  return {
    stackedBlocksAllowed: layout === 'stacked',
    maxPageWeight: budget.maxPageWeight,
    usablePageWeight: budget.usablePageWeight,
    pageBaseWeight: budget.pageBaseWeight,
    blockGapWeight: Number.isFinite(measuredBlockGap) && measuredBlockGap >= 0 ? measuredBlockGap : 0,
    profile: budget.profile,
  };
}

function pushFlowPage(
  pages: PagedFlipPage[],
  collection: MenuCollection,
  layout: PagedFlipContentLayout,
  blocks: FlowCategoryBlock[],
  maxPageWeight: number,
  pageBaseWeight: number,
) {
  if (blocks.length === 0) return;

  const eyebrowValues = [...new Set(blocks.map((block) => block.eyebrow).filter(Boolean))];
  const singleBlock = blocks.length === 1 ? blocks[0] : undefined;

  pages.push({
    id: `${collection.id}-flow-${pages.length + 1}`,
    kind: 'content',
    layout,
    pageNumber: pages.length + 1,
    title: singleBlock?.name || collection.name,
    subtitle: singleBlock?.description || collection.description,
    eyebrow: eyebrowValues.length === 1 ? eyebrowValues[0] : collection.name,
    chapterId: collection.id,
    chapterLabel: collection.name,
    categories: blocks.map((block) => enrichCategoryBlock(block, Math.max(0, maxPageWeight - pageBaseWeight))),
    metrics: buildPageMetrics(blocks, maxPageWeight, pageBaseWeight),
  });
}

function categoryFitsFreshPage(
  category: Category,
  config: PagedFlipRuntimeConfig,
  viewport: ViewportMode,
  layout: PagedFlipContentLayout,
) {
  const { usablePageWeight, profile } = resolveFlowLimits(config, viewport, layout);
  const items = (category.items || []).map((item) => toPageItem(item, category, config));
  const totalItemWeight = items.reduce(
    (sum, item) => sum + resolveItemWeight(item, profile, config, viewport, layout, 'content'),
    0,
  );
  const totalWeight =
    resolveCategoryHeaderWeight(category, viewport, profile, config, layout, 'content', false) +
    totalItemWeight +
    resolveCategoryFooterWeight(category, viewport, config, layout, 'content', false);

  return totalWeight <= usablePageWeight;
}

function pushCategoryIntoFlowPages(
  pages: PagedFlipPage[],
  collection: MenuCollection,
  category: Category,
  config: PagedFlipRuntimeConfig,
  viewport: ViewportMode,
  state: {
    blocks: FlowCategoryBlock[];
    weight: number;
    layout: PagedFlipContentLayout;
  },
  options?: {
    eyebrow?: string;
  },
) {
  const limits = resolveFlowLimits(config, viewport, state.layout);
  const singleBlockPerPage = !limits.stackedBlocksAllowed;
  const headerWeight = resolveCategoryHeaderWeight(
    category,
    viewport,
    limits.profile,
    config,
    state.layout,
    'content',
    false,
  );
  const continuationHeaderWeight = resolveCategoryHeaderWeight(
    category,
    viewport,
    limits.profile,
    config,
    state.layout,
    'content',
    true,
  );
  const footerWeight = resolveCategoryFooterWeight(
    category,
    viewport,
    config,
    state.layout,
    'content',
    false,
  );
  const continuationFooterWeight = resolveCategoryFooterWeight(
    category,
    viewport,
    config,
    state.layout,
    'content',
    true,
  );
  const items = (category.items || []).map((item) => {
    const pageItem = toPageItem(item, category, config);
    return {
      ...pageItem,
      estimatedWeight: resolveItemWeight(pageItem, limits.profile, config, viewport, state.layout, 'content'),
    };
  });

  const flushCurrentPage = () => {
    if (state.blocks.length === 0) return;
    pushFlowPage(pages, collection, state.layout, state.blocks, limits.maxPageWeight, limits.pageBaseWeight);
    state.blocks = [];
    state.weight = 0;
  };

  if (items.length === 0) {
    const leadingGapWeight = state.blocks.length > 0 ? limits.blockGapWeight : 0;
    const emptyBlock: FlowCategoryBlock = {
      id: `${category.id}-empty-${pages.length + state.blocks.length + 1}`,
      sourceCategoryId: category.id,
      name: category.name,
      description: config.showItemDescriptions === false ? undefined : category.description,
      image: category.image,
      promoLabel: resolveCategoryPromoLabel(category, config),
      promotion: category.promotion,
      items: [],
      eyebrow: options?.eyebrow,
      estimatedWeight: headerWeight + footerWeight,
      startsAt: 0,
      endsAt: 0,
      totalItems: 0,
    };

    if (
      state.blocks.length > 0 &&
      (singleBlockPerPage || state.weight + leadingGapWeight + emptyBlock.estimatedWeight > limits.usablePageWeight)
    ) {
      flushCurrentPage();
    }

    state.blocks.push(emptyBlock);
    state.weight += (state.blocks.length > 1 ? limits.blockGapWeight : 0) + emptyBlock.estimatedWeight;
    return;
  }

  const shouldStartFreshPage =
    (config.contentFlow === 'category-first' || config.keepCategoryTogether) &&
    state.blocks.length > 0 &&
    categoryFitsFreshPage(category, config, viewport, state.layout);

  if (shouldStartFreshPage || (singleBlockPerPage && state.blocks.length > 0)) {
    flushCurrentPage();
  }

  let itemIndex = 0;
  let continuation = false;
  const totalItems = items.length;

  while (itemIndex < items.length) {
    const currentItem = items[itemIndex];
    if (!currentItem) break;

    let availableWeight = limits.usablePageWeight - state.weight;
    const leadingGapWeight = state.blocks.length > 0 ? limits.blockGapWeight : 0;
    const activeHeaderWeight = continuation ? continuationHeaderWeight : headerWeight;
    const activeFooterWeight = continuation ? continuationFooterWeight : footerWeight;
    const minimumBlockWeight =
      activeHeaderWeight +
      (currentItem.estimatedWeight ??
        resolveItemWeight(currentItem, limits.profile, config, viewport, state.layout, 'content')) +
      activeFooterWeight;

    if (
      state.blocks.length > 0 &&
      (singleBlockPerPage || availableWeight - leadingGapWeight < minimumBlockWeight)
    ) {
      flushCurrentPage();
      availableWeight = limits.usablePageWeight;
    }

    const blockItems: PagedFlipPageItem[] = [];
    let blockWeight = activeHeaderWeight;
    const startsAt = itemIndex;
    const blockAvailableWeight = Math.max(0, availableWeight - (state.blocks.length > 0 ? limits.blockGapWeight : 0));

    while (itemIndex < items.length) {
      const nextItem = items[itemIndex];
      if (!nextItem) break;

      const nextItemWeight =
        nextItem.estimatedWeight ??
        resolveItemWeight(nextItem, limits.profile, config, viewport, state.layout, 'content');
      const overflowsCurrentPage =
        blockItems.length > 0 && blockWeight + nextItemWeight + activeFooterWeight > blockAvailableWeight;

      if (overflowsCurrentPage) {
        break;
      }

      blockItems.push(nextItem);
      blockWeight += nextItemWeight;
      itemIndex += 1;

      if (blockWeight >= availableWeight) {
        break;
      }
    }

    if (blockItems.length === 0) {
      blockItems.push(currentItem);
      blockWeight +=
        currentItem.estimatedWeight ??
        resolveItemWeight(currentItem, limits.profile, config, viewport, state.layout, 'content');
      itemIndex += 1;
    }

    state.blocks.push({
      id: `${category.id}-${pages.length + 1}-${state.blocks.length + 1}`,
      sourceCategoryId: category.id,
      name: category.name,
      description: config.showItemDescriptions === false ? undefined : category.description,
      image: category.image,
      promoLabel: resolveCategoryPromoLabel(category, config),
      promotion: category.promotion,
      items: blockItems,
      continuation,
      eyebrow: options?.eyebrow,
      estimatedWeight: blockWeight + activeFooterWeight,
      startsAt,
      endsAt: itemIndex - 1,
      totalItems,
    });
    
    state.weight += (state.blocks.length > 1 ? limits.blockGapWeight : 0) + blockWeight + activeFooterWeight;
    continuation = true;

    if (itemIndex < items.length) {
      flushCurrentPage();
      continue;
    }

    if (singleBlockPerPage) {
      flushCurrentPage();
    }
  }
}

function resolveCategoryRule(
  config: ReturnType<typeof resolvePagedFlipConfig>,
  categoryIndex: number,
): PagedFlipCategoryRule | undefined {
  return (config.categoryRules || []).find((rule) => rule.matchIndex === categoryIndex + 1);
}

function resolveCategoryLayout(
  config: ReturnType<typeof resolvePagedFlipConfig>,
  rule: PagedFlipCategoryRule | undefined,
  viewport: ViewportMode,
): PagedFlipContentLayout {
  if (rule?.layout === 'two-column-items' && viewport === 'desktop') {
    return 'two-column-items';
  }

  if (config.contentLayout === 'two-column-items' && viewport === 'desktop') {
    return 'two-column-items';
  }

  return 'stacked';
}

function resolveRuleSpotlightImage(
  category: Category,
  config: ReturnType<typeof resolvePagedFlipConfig>,
  rule: PagedFlipCategoryRule | undefined,
): string | undefined {
  if (rule?.spotlightImageSource === 'none') return undefined;
  if (rule?.spotlightImageSource === 'category') {
    return resolveValidCategoryImage(category) || resolveFirstValidItemImage(category);
  }
  if (rule?.spotlightImageSource === 'first-item') {
    return resolveFirstValidItemImage(category);
  }
  return resolveSpotlightImage(category, config);
}

function createCategoryEntries(
  collection: MenuCollection,
  useSections: boolean,
): CollectionCategoryEntry[] {
  if (!useSections) {
    return (collection.categories || []).map((category, index) => ({ category, index }));
  }

  const entries: CollectionCategoryEntry[] = [];
  const sections = collection.sections || [];
  sections.forEach((section) => {
    (section.categories || []).forEach((category) => {
      entries.push({
        category,
        eyebrow: section.name,
        section,
        index: entries.length,
      });
    });
  });

  return entries;
}

function createCategorySpotlightPage(
  category: Category,
  collection: MenuCollection,
  pages: PagedFlipPage[],
  config: ReturnType<typeof resolvePagedFlipConfig>,
  options?: {
    eyebrow?: string;
    spotlightImage?: string;
  },
): PagedFlipPage {
  return {
    id: `${category.id}-spotlight`,
    kind: 'category-spotlight',
    pageNumber: pages.length + 1,
    title: category.name,
    subtitle: category.description,
    eyebrow: options?.eyebrow || collection.name,
    chapterId: collection.id,
    chapterLabel: collection.name,
    spotlightCategoryId: category.id,
    spotlightImage: options?.spotlightImage,
  };
}

function pushCategoryContentPages(
  pages: PagedFlipPage[],
  collection: MenuCollection,
  category: Category,
  config: PagedFlipRuntimeConfig,
  viewport: ViewportMode,
  overrides?: {
    kind?: ContentPageKind;
    title?: string;
    subtitle?: string;
    eyebrow?: string;
    layout?: PagedFlipContentLayout;
  },
) {
  const layout: PagedFlipContentLayout = overrides?.layout || resolveCategoryLayout(config, undefined, viewport);
  const kind = overrides?.kind || 'content';
  const budget = resolvePageWeightBudget(config, viewport, layout, kind);
  const blocks = buildCategoryBlocks(category, config, viewport, layout, kind);
  const bucketKey = resolveMeasurementBucketKey(config, viewport, kind, layout);
  const measuredGap = Number(config.measurements?.blockGapWeights?.[bucketKey]);
  const blockGapWeight = Number.isFinite(measuredGap) && measuredGap >= 0 ? measuredGap : 0;

  let index = 0;
  while (index < blocks.length) {
    const nextBlocks: WeightedCategoryBlock[] = [];
    let nextWeight = 0;

    while (index < blocks.length) {
      const candidate = blocks[index];
      if (!candidate) break;

      const gapWeight = nextBlocks.length > 0 ? blockGapWeight : 0;
      const candidateWeight = nextWeight + gapWeight + candidate.estimatedWeight;

      if (nextBlocks.length > 0 && candidateWeight > budget.usablePageWeight) {
        break;
      }

      nextBlocks.push(candidate);
      nextWeight = candidateWeight;
      index += 1;
    }

    // Look-ahead: if a single block alone on a page would leave the page
    // under-filled (less than 55% of usable weight), try to pull the first
    // item from the next block so the current page has more content instead
    // of the next page being nearly empty.
    if (
      nextBlocks.length === 1 &&
      index < blocks.length &&
      nextWeight < budget.usablePageWeight * 0.55
    ) {
      const nextBlock = blocks[index];
      if (nextBlock && nextBlock.items.length > 1) {
        const pulledItem = nextBlock.items[0]!;
        const pulledWeight = resolveItemWeight(pulledItem, budget.profile, config, viewport, layout, kind);
        const gapForPulled = blockGapWeight;
        if (nextWeight + gapForPulled + pulledWeight <= budget.usablePageWeight) {
          const continuationHeaderWeight = resolveCategoryHeaderWeight(
            category, viewport, budget.profile, config, layout, kind, true,
          );
          const continuationFooterWeight = resolveCategoryFooterWeight(
            category, viewport, config, layout, kind, true,
          );
          // Add the pulled item to the current page's block
          const targetBlock = nextBlocks[0]!;
          targetBlock.items = [...targetBlock.items, pulledItem];
          targetBlock.estimatedWeight = nextWeight + gapForPulled + pulledWeight + continuationFooterWeight;
          targetBlock.endsAt = targetBlock.startsAt + targetBlock.items.length - 1;
          nextWeight = targetBlock.estimatedWeight;
          // Trim the item from the next block
          const remainingItems = nextBlock.items.slice(1);
          if (remainingItems.length === 0) {
            index += 1;
          } else {
            const remainingWeight = remainingItems.reduce(
              (sum, item) => sum + resolveItemWeight(item, budget.profile, config, viewport, layout, kind),
              0,
            );
            blocks[index] = {
              ...nextBlock,
              items: remainingItems,
              estimatedWeight: continuationHeaderWeight + remainingWeight + continuationFooterWeight,
              startsAt: nextBlock.startsAt + 1,
              endsAt: nextBlock.startsAt + remainingItems.length,
            };
          }
        }
      }
    }

    if (nextBlocks.length === 0) {
      nextBlocks.push(blocks[index]!);
      index += 1;
    }

    pages.push({
      id: `${collection.id}-${category.id}-${kind}-${pages.length + 1}`,
      kind,
      layout,
      pageNumber: pages.length + 1,
      title: overrides?.title || category.name,
      subtitle: overrides?.subtitle ?? (config.showItemDescriptions === false ? undefined : category.description),
      eyebrow: overrides?.eyebrow,
      chapterId: collection.id,
      chapterLabel: collection.name,
      categories: nextBlocks.map((block) => enrichCategoryBlock(block, budget.usablePageWeight)),
      metrics: buildPageMetrics(nextBlocks, budget.maxPageWeight, budget.pageBaseWeight),
    });
  }
}

function createPseudoCategory(id: string, name: string, description: string | undefined, items: Item[]): Category {
  return {
    id,
    name,
    description,
    items,
  };
}

function buildFeaturedDescription(items: Item[]): string | undefined {
  if (items.length === 0) return undefined;
  return 'Highlighted dishes and drinks we want guests to notice first.';
}

function buildNewItemsDescription(items: Item[], windowDays: number): string {
  const comingSoonCount = items.filter((item) => isComingSoonItem(item, windowDays)).length;
  const newCount = items.filter((item) => isNewItem(item, windowDays)).length;

  if (comingSoonCount > 0 && newCount > 0) {
    return `Fresh additions from the last ${windowDays} days, plus ${comingSoonCount} coming soon ${comingSoonCount === 1 ? 'item' : 'items'}.`;
  }

  if (comingSoonCount > 0) {
    return `${comingSoonCount} upcoming ${comingSoonCount === 1 ? 'item is' : 'items are'} marked coming soon.`;
  }

  if (newCount > 0) {
    return `Fresh additions from the last ${windowDays} days.`;
  }

  return `Recent and upcoming menu changes from the last ${windowDays} days.`;
}

function getNewItems(items: Item[], windowDays: number): Item[] {
  return [...items]
    .map((item) => {
      const createdAt = item.created_at ? new Date(item.created_at).getTime() : Number.NaN;
      const availableFrom = item.available_from ? new Date(item.available_from).getTime() : Number.NaN;
      const fresh = isNewItem(item, windowDays);
      const comingSoon = isComingSoonItem(item, windowDays);

      return {
        item,
        fresh,
        comingSoon,
        sortTimestamp:
          comingSoon && Number.isFinite(availableFrom)
            ? availableFrom
            : Number.isFinite(createdAt)
              ? createdAt
              : Number.POSITIVE_INFINITY,
      };
    })
    .filter((entry) => entry.fresh || entry.comingSoon)
    .sort((left, right) => {
      if (left.comingSoon !== right.comingSoon) {
        return left.comingSoon ? -1 : 1;
      }
      return left.comingSoon
        ? left.sortTimestamp - right.sortTimestamp
        : right.sortTimestamp - left.sortTimestamp;
    })
    .map((entry) => ({
      ...entry.item,
      is_new: entry.fresh,
      coming_soon: entry.comingSoon,
      is_coming_soon: entry.item.is_coming_soon || entry.comingSoon,
    }));
}

function pushShowcasePages(
  pages: PagedFlipPage[],
  collection: MenuCollection,
  config: PagedFlipRuntimeConfig,
  viewport: ViewportMode,
  options: {
    kind: 'featured-showcase' | 'new-arrivals';
    title: string;
    subtitle?: string;
    eyebrow: string;
    items: Item[];
    layoutMode?: PagedFlipCategoryLayout;
  },
) {
  if (options.items.length === 0) return;

  const pseudoCategory = createPseudoCategory(
    `${collection.id}-${options.kind}`,
    options.kind === 'featured-showcase' ? 'Highlights' : 'Fresh arrivals',
    options.subtitle,
    options.items,
  );

  const layoutMode = options.layoutMode || 'single-page';
  // Showcase pages already render their item cards directly. A separate
  // pseudo-category intro often becomes a title-only page in custom slots.

  pushCategoryContentPages(pages, collection, pseudoCategory, config, viewport, {
    kind: options.kind,
    title: options.title,
    subtitle: options.subtitle,
    eyebrow: options.eyebrow,
    layout:
      layoutMode === 'two-column-items'
        ? resolveCategoryLayout({ ...config, contentLayout: 'two-column-items' }, undefined, viewport)
        : 'stacked',
  });
}

function buildPromotionPageSubtitle(collection: MenuCollection, promoCount: number) {
  if (promoCount <= 0) {
    return collection.description || 'No active promotions are available for this chapter yet.';
  }

  if (promoCount === 1) {
    return `One active offer is currently available in ${collection.name}.`;
  }

  return `${promoCount} active offers are currently available in ${collection.name}.`;
}

function collectCollectionPromotions(collection: MenuCollection) {
  const promotionMap = new Map<string, NonNullable<MenuCollection['promotions']>[number]>();

  (collection.promotions || []).forEach((promotion) => {
    if (promotion?.id && isPromotionActive(promotion)) promotionMap.set(promotion.id, promotion);
  });

  (collection.categories || []).forEach((category) => {
    if (category.promotion?.id && isPromotionActive(category.promotion)) {
      promotionMap.set(category.promotion.id, category.promotion);
    }

    (category.items || []).forEach((item) => {
      if (item.promotion?.id && isPromotionActive(item.promotion)) {
        promotionMap.set(item.promotion.id, item.promotion);
      }
    });
  });

  return Array.from(promotionMap.values());
}

function pushPromotionShowcasePage(
  pages: PagedFlipPage[],
  collection: MenuCollection,
  config: PagedFlipRuntimeConfig,
) {
  const promotions = collectCollectionPromotions(collection);
  if (!config.promotionPages?.enabled || promotions.length === 0) return;

  pages.push({
    id: `${collection.id}-promotion-showcase`,
    kind: 'promotion-showcase',
    pageNumber: pages.length + 1,
    title: config.promotionPages.title || 'Current Offers',
    subtitle:
      config.promotionPages.subtitle ||
      buildPromotionPageSubtitle(collection, promotions.length),
    eyebrow: config.promotionPages.eyebrow || collection.name,
    chapterId: collection.id,
    chapterLabel: collection.name,
    promotions,
    metaRows: [
      { label: 'Offers', value: String(promotions.length) },
      { label: 'Chapter', value: collection.name },
    ],
  });
}

function pushSectionDividerPage(
  pages: PagedFlipPage[],
  collection: MenuCollection,
  section: MenuSection,
  categoryCount: number,
  isOnlySection: boolean,
) {
  if (isOnlySection && section.id === 'default') return;

  pages.push({
    id: `${collection.id}-${section.id}-divider`,
    kind: 'section-divider',
    pageNumber: pages.length + 1,
    title: section.name,
    subtitle: section.description || `Explore ${categoryCount} ${categoryCount === 1 ? 'category' : 'categories'} in this section.`,
    eyebrow: collection.name,
    chapterId: collection.id,
    chapterLabel: collection.name,
    metaRows: [
      { label: 'Categories', value: String(categoryCount) },
      { label: 'Featured', value: String(section.featuredItems?.length || 0) },
    ],
  });
}

function buildCollectionPages(
  pages: PagedFlipPage[],
  collection: MenuCollection,
  config: PagedFlipRuntimeConfig,
  viewport: ViewportMode,
) {
  const promotionInsertPosition = config.promotionPages?.insertPosition || 'before-content';
  const sections = collection.sections?.length ? collection.sections : [];
  const useSections = config.showSectionDividerPages && sections.length > 0;
  const entries = createCategoryEntries(collection, useSections);
  const defaultSpotlightLayout = config.showCategorySpotlights !== false;
  const defaultSpotlightAllowed = defaultSpotlightLayout;
  const featuredInsertPosition = config.featuredInsertPosition || 'after-category';
  const newItemsInsertPosition = config.newItemsInsertPosition || 'after-category';
  const featuredInsertBeforeIndex = config.featuredInsertBeforeCategoryIndex;
  const featureInsertIndex = config.featuredInsertAfterCategoryIndex;
  const newItemsInsertBeforeIndex = config.newItemsInsertBeforeCategoryIndex;
  const newInsertIndex = config.newItemsInsertAfterCategoryIndex;
  const newItemWindowDays = Number(config.newItemWindowDays || DEFAULT_NEW_ITEM_WINDOW_DAYS);
  const allItems = collection.items || [];
  let newItems = config.showNewItemPages ? getNewItems(allItems, newItemWindowDays) : [];
  // When every item on the menu is "new" (e.g. a brand-new branch that just
  // loaded all its items), showing a "New & Noteworthy" showcase page adds no
  // value , the items will appear in their normal category pages anyway.
  // Suppress the new-arrivals section if new items represent ≥90 % of the menu.
  if (newItems.length > 0 && allItems.length > 0 && newItems.length >= allItems.length * 0.9) {
    newItems = [];
  }
  const flowState = {
    blocks: [] as FlowCategoryBlock[],
    weight: 0,
    layout: resolveCategoryLayout(config, undefined, viewport),
  };
  let currentSectionId: string | undefined;
  let featuredInserted = false;
  let newItemsInserted = false;
  const flowLimits = resolveFlowLimits(config, viewport, flowState.layout);

  const flushFlowBlocks = () => {
    if (flowState.blocks.length === 0) return;
    pushFlowPage(
      pages,
      collection,
      flowState.layout,
      flowState.blocks,
      flowLimits.maxPageWeight,
      flowLimits.pageBaseWeight,
    );
    flowState.blocks = [];
    flowState.weight = 0;
  };

  const maybeInsertShowcase = (position: 'before-category' | 'after-category', categoryOrdinal: number) => {
    if (
      !featuredInserted &&
      position === featuredInsertPosition &&
      ((position === 'before-category' && featuredInsertBeforeIndex === categoryOrdinal) ||
        (position === 'after-category' && featureInsertIndex === categoryOrdinal)) &&
      config.showFeaturedItemPages &&
      (collection.featuredItems || []).length > 0
    ) {
      flushFlowBlocks();
      pushShowcasePages(pages, collection, config, viewport, {
        kind: 'featured-showcase',
        title: 'Featured Highlights',
        subtitle: buildFeaturedDescription(collection.featuredItems || []),
        eyebrow: collection.name,
        items: collection.featuredItems || [],
        layoutMode: config.featuredLayout || 'single-page',
      });
      featuredInserted = true;
    }

    if (
      !newItemsInserted &&
      position === newItemsInsertPosition &&
      ((position === 'before-category' && newItemsInsertBeforeIndex === categoryOrdinal) ||
        (position === 'after-category' && newInsertIndex === categoryOrdinal)) &&
      newItems.length > 0
    ) {
      flushFlowBlocks();
      pushShowcasePages(pages, collection, config, viewport, {
        kind: 'new-arrivals',
        title: 'New & Noteworthy',
        subtitle: buildNewItemsDescription(newItems, newItemWindowDays),
        eyebrow: collection.name,
        items: newItems,
        layoutMode: config.newItemsLayout || 'single-page',
      });
      newItemsInserted = true;
    }
  };

  if (
    featuredInsertBeforeIndex == null &&
    featureInsertIndex == null &&
    config.showFeaturedItemPages &&
    (collection.featuredItems || []).length > 0
  ) {
    pushShowcasePages(pages, collection, config, viewport, {
      kind: 'featured-showcase',
      title: 'Featured Highlights',
      subtitle: buildFeaturedDescription(collection.featuredItems || []),
      eyebrow: collection.name,
      items: collection.featuredItems || [],
      layoutMode: config.featuredLayout || 'single-page',
    });
    featuredInserted = true;
  }

  if (newItemsInsertBeforeIndex == null && newInsertIndex == null && newItems.length > 0) {
    pushShowcasePages(pages, collection, config, viewport, {
      kind: 'new-arrivals',
      title: 'New & Noteworthy',
      subtitle: buildNewItemsDescription(newItems, newItemWindowDays),
      eyebrow: collection.name,
      items: newItems,
      layoutMode: config.newItemsLayout || 'single-page',
    });
    newItemsInserted = true;
  }

  if (promotionInsertPosition === 'before-content') {
    pushPromotionShowcasePage(pages, collection, config);
  }

  entries.forEach((entry, entryIndex) => {
    const { category, eyebrow, section } = entry;
    const categoryOrdinal = entryIndex + 1;
    const rule = resolveCategoryRule(config, entryIndex);
    const categoryLayout = rule?.layout || 'default';
    const spotlightImage = resolveRuleSpotlightImage(category, config, rule);
    // Always create intro pages for category-spread layouts , mark them
    // hideOnMobile instead of conditionally skipping. This lets the renderer
    // handle viewport-specific filtering dynamically.
    const hasSpotlightContent = hasRenderableText(category.description);
    const shouldRenderIntroPage =
      categoryLayout === 'category-spread' || (defaultSpotlightAllowed && hasSpotlightContent);
    const introPageHideOnMobile =
      categoryLayout === 'category-spread' && rule?.hideIntroOnMobile !== false;

    if (useSections && section?.id && section.id !== currentSectionId) {
      flushFlowBlocks();
      currentSectionId = section.id;
      pushSectionDividerPage(
        pages,
        collection,
        section,
        section.categories?.length || 0,
        sections.length === 1,
      );
    }

    maybeInsertShowcase('before-category', categoryOrdinal);

    if (shouldRenderIntroPage) {
      flushFlowBlocks();
      pages.push({
        ...createCategorySpotlightPage(category, collection, pages, config, {
          eyebrow: eyebrow || collection.name,
          spotlightImage,
        }),
        hideOnMobile: introPageHideOnMobile || undefined,
      });
    }

    if (config.contentFlow === 'flow' && categoryLayout === 'default') {
      pushCategoryIntoFlowPages(pages, collection, category, config, viewport, flowState, {
        eyebrow,
      });
    } else {
      flushFlowBlocks();
      pushCategoryContentPages(pages, collection, category, config, viewport, {
        eyebrow,
        layout: resolveCategoryLayout(
          categoryLayout === 'two-column-items'
            ? { ...config, contentLayout: 'two-column-items' }
            : config,
          rule,
          viewport,
        ),
      });
    }

    maybeInsertShowcase('after-category', categoryOrdinal);
  });

  flushFlowBlocks();

  if (!featuredInserted && config.showFeaturedItemPages && (collection.featuredItems || []).length > 0) {
    pushShowcasePages(pages, collection, config, viewport, {
      kind: 'featured-showcase',
      title: 'Featured Highlights',
      subtitle: buildFeaturedDescription(collection.featuredItems || []),
      eyebrow: collection.name,
      items: collection.featuredItems || [],
      layoutMode: config.featuredLayout || 'single-page',
    });
  }

  if (!newItemsInserted && newItems.length > 0) {
    pushShowcasePages(pages, collection, config, viewport, {
        kind: 'new-arrivals',
        title: 'New & Noteworthy',
        subtitle: buildNewItemsDescription(newItems, newItemWindowDays),
      eyebrow: collection.name,
      items: newItems,
      layoutMode: config.newItemsLayout || 'single-page',
    });
  }

  if (promotionInsertPosition === 'after-content') {
    pushPromotionShowcasePage(pages, collection, config);
  }
}

function maybeAddBackCover(
  pages: PagedFlipPage[],
  config: ReturnType<typeof resolvePagedFlipConfig>,
  title: string,
  menuData: MenuData,
) {
  if (!config.showBackCover) return;

  pages.push({
    id: 'back-cover',
    kind: 'back-cover',
    pageNumber: pages.length + 1,
    title: config.backCover?.note || `Thank you for exploring ${title}`,
    subtitle: config.backCover?.cta || config.backCover?.footer,
    eyebrow: menuData.business?.name,
    metaRows: buildMetaRows(menuData).slice(1),
  });
}

export function buildPagedFlipDocument(
  template: Template,
  menuData: MenuData,
  options?: {
    viewport?: ViewportMode;
    pageHeight?: number;
    measurementScopeKey?: string;
    measurements?: PagedFlipMeasurementSnapshot;
  },
): PagedFlipDocument {
  const config = resolvePagedFlipConfig(template) as PagedFlipRuntimeConfig;
  if (options?.pageHeight !== undefined) config.pageHeight = options.pageHeight;
  if (options?.measurementScopeKey !== undefined) config.measurementScopeKey = options.measurementScopeKey;
  if (options?.measurements !== undefined) config.measurements = options.measurements;
  
  const viewport = options?.viewport || 'desktop';
  const title = menuData.menu.title || template.meta.name;
  const subtitle =
    menuData.business?.tagline ||
    menuData.business?.branchDescription ||
    menuData.menu.description ||
    menuData.business?.parentName ||
    undefined;
  const collections = getCollections(menuData, config.useCollectionsAsChapters);
  const hasCollectionChapters = config.useCollectionsAsChapters && collections.length > 1;
  const pages: PagedFlipPage[] = [];
  const metaRows = buildMetaRows(menuData);

  if (config.showCoverPage) {
    pages.push({
      id: 'cover',
      kind: 'cover',
      pageNumber: 1,
      title,
      subtitle,
      eyebrow: menuData.business?.name,
      metaRows,
    });
  }

  let tocPageIndex = -1;
  if (hasCollectionChapters && config.showTableOfContents) {
    tocPageIndex = pages.length;
    pages.push({
      id: 'toc',
      kind: 'toc',
      pageNumber: pages.length + 1,
      title: 'Menu Guide',
      subtitle: 'Choose a chapter to jump in.',
      eyebrow: menuData.business?.parentName || menuData.business?.name,
      tocEntries: [],
    });
  }

  const chapters: PagedFlipChapter[] = [];

  collections.forEach((collection) => {
    const startPage = pages.length + 1;
    chapters.push({
      id: collection.id,
      label: collection.name,
      description: collection.description,
      startPage,
    });

    if (hasCollectionChapters && config.chapterIntroPages) {
      pages.push({
        id: `${collection.id}-chapter`,
        kind: 'chapter',
        pageNumber: pages.length + 1,
        title: collection.name,
        subtitle: collection.description || `Explore ${collection.name.toLowerCase()}`,
        eyebrow: menuData.business?.name,
        chapterId: collection.id,
        chapterLabel: collection.name,
      });
    }

    buildCollectionPages(pages, collection, config, viewport);
  });

  if (tocPageIndex >= 0) {
    pages[tocPageIndex] = {
      ...pages[tocPageIndex],
      tocEntries: chapters.map((chapter) => ({
        id: chapter.id,
        label: chapter.label,
        pageNumber: chapter.startPage,
      })),
    };
  }

  maybeAddBackCover(pages, config, title, menuData);

  return {
    title,
    subtitle,
    chapters,
    pages: resequencePagedFlipPages(pages),
  };
}
